/**
 * Rail — фиксированная «приборная панель» страницы как в референсе:
 *   · справа по центру — точки-навигация по секциям (desktop)
 *   · справа сверху — % прокрутки
 * Scroll-spy через IntersectionObserver.
 */
import { useEffect, useState } from "react";
import { useScrollProgress } from "../lib/motion";
import { useLang } from "../i18n";

const SECTIONS = ["top", "process", "apps", "waiter", "features", "case", "pricing", "builder", "faq", "lead"] as const;

export function Rail() {
  const { t } = useLang();
  const progress = useScrollProgress();
  const [active, setActive] = useState<string>("top");

  useEffect(() => {
    const els = SECTIONS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* % прокрутки */}
      <div
        className="fixed right-5 top-24 z-[85] hidden text-right lg:block"
        aria-hidden
      >
        <span className="t-brand text-xs" style={{ color: "var(--fg-muted)" }}>
          {String(Math.round(progress * 100)).padStart(3, "0")}%
        </span>
        <div
          className="ml-auto mt-2 h-px w-10"
          style={{
            background: `linear-gradient(90deg, transparent, var(--gold) ${progress * 100}%)`,
          }}
        />
      </div>

      {/* Точки-навигация */}
      <nav
        className="fixed right-5 top-1/2 z-[85] hidden -translate-y-1/2 flex-col items-center gap-3 lg:flex"
        aria-label={t.rail.ariaLabel}
      >
        {SECTIONS.map((id) => {
          const isActive = id === active;
          return (
            <a
              key={id}
              href={`#${id}`}
              aria-label={id}
              className="block rounded-full transition-all duration-300"
              style={{
                width: isActive ? 7 : 5,
                height: isActive ? 22 : 5,
                background: isActive ? "var(--gold)" : "rgba(244,239,230,0.25)",
                boxShadow: isActive ? "0 0 10px rgba(212,166,86,0.7)" : "none",
              }}
            />
          );
        })}
      </nav>
    </>
  );
}
